import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PaginatedResponse } from '@/config/api';
import type {
  Product,
  ProductCategory,
  Cart,
  Order,
  TrackingInfo,
  ProductLabelData,
  EnhancedTrackingInfo,
  ProofOfDeliveryData,
  OrderModification,
  RecurringOrder,
  OrderTemplate,
  ParLevelConfig,
  ParCheckResult,
  DeliveryCalendarEvent,
  DeliveryFeedbackData,
  QualityTrend,
  ClaimSummary,
  ClaimDetail,
  SubstitutionPref,
  SubstitutionHistoryItem,
  ShortageAlertData,
  LiveTrackingData,
} from '@/types';
import type { SubstitutionRule } from '@/types/api';
import { fetchOne, api } from './helpers';
import { useCartStore } from '@/store/cart';

// ---------------------------------------------------------------------------
// Products / Catalog
// ---------------------------------------------------------------------------

export function useProductCategories() {
  return useQuery({
    queryKey: ['product-categories'],
    queryFn: () => fetchOne<ProductCategory[]>('/catalog/categories'),
  });
}

export function useProducts(params?: {
  search?: string;
  category?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['portal-products', params],
    queryFn: () => fetchOne<PaginatedResponse<Product>>('/catalog/products', params),
  });
}

export function useFeaturedProducts() {
  return useQuery({
    queryKey: ['featured-products'],
    queryFn: () => fetchOne<Product[]>('/catalog/featured'),
  });
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export function useProductLabelData(
  productId: number,
  params?: { lot_id?: number; barcode_type?: string },
) {
  return useQuery({
    queryKey: ['product-label', productId, params],
    queryFn: () =>
      fetchOne<ProductLabelData>(
        `/catalog/products/${productId}/label-data`,
        params as Record<string, unknown>,
      ),
    enabled: productId > 0,
  });
}

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

export function useCart() {
  const setItemCount = useCartStore.getState().setItemCount;
  return useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const data = await fetchOne<Cart>('/cart');
      setItemCount(data.item_count);
      return data;
    },
  });
}

export function useAddToCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: {
      product_id: number;
      quantity: number;
      unit?: string;
      notes?: string;
    }) => api.post('/cart/items', item),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useUpdateCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: number; quantity: number }) =>
      api.patch(`/cart/items/${itemId}`, { quantity }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useRemoveCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) => api.delete(`/cart/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useClearCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/cart'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useApplyPromo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post('/cart/promo', { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: {
      delivery_date?: string;
      delivery_address_id?: number;
      payment_method?: string;
      po_number?: string;
      notes?: string;
    }) => api.post('/cart/checkout', req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export function useOrders(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => fetchOne<{ items: Order[]; total: number }>('/orders', params),
  });
}

export function useOrder(id: number) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => fetchOne<Order>(`/orders/${id}`),
    enabled: id > 0,
  });
}

export function useReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => api.post(`/orders/${orderId}/reorder`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: number; reason?: string }) =>
      api.post(`/orders/${orderId}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Tracking
// ---------------------------------------------------------------------------

export function useTracking(orderId: number) {
  return useQuery({
    queryKey: ['tracking', orderId],
    queryFn: () => fetchOne<TrackingInfo>(`/tracking/track/${orderId}`),
    enabled: orderId > 0,
  });
}

// ---------------------------------------------------------------------------
// Enhanced Tracking / POD
// ---------------------------------------------------------------------------

export function useEnhancedTracking(orderId: number) {
  return useQuery({
    queryKey: ['enhanced-tracking', orderId],
    queryFn: () => fetchOne<EnhancedTrackingInfo>(`/tracking/track/${orderId}/enhanced`),
    enabled: orderId > 0,
  });
}

export function useProofOfDelivery(orderId: number) {
  return useQuery({
    queryKey: ['pod', orderId],
    queryFn: () => fetchOne<ProofOfDeliveryData>(`/tracking/track/${orderId}/pod`),
    enabled: orderId > 0,
  });
}

export function useShareTrackingLink() {
  return useMutation({
    mutationFn: (orderId: number) =>
      api.post<{ tracking_url: string }>(`/tracking/track/${orderId}/share`),
  });
}

// ---------------------------------------------------------------------------
// Order Modifications
// ---------------------------------------------------------------------------

export function useSubmitOrderModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      ...data
    }: {
      orderId: number;
      modifications: Record<string, unknown>[];
      notes?: string;
    }) => api.post(`/orders/${orderId}/modify`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order-modifications'] });
    },
  });
}

export function useOrderModifications(orderId: number) {
  return useQuery({
    queryKey: ['order-modifications', orderId],
    queryFn: () => fetchOne<OrderModification[]>(`/orders/${orderId}/modifications`),
    enabled: orderId > 0,
  });
}

export function useOrderModifyCutoff(orderId: number) {
  return useQuery({
    queryKey: ['order-modify-cutoff', orderId],
    queryFn: () =>
      fetchOne<{
        cutoff_at: string | null;
        can_modify: boolean;
        reason: string;
      }>(`/orders/${orderId}/modify/cutoff`),
    enabled: orderId > 0,
  });
}

export function useCancelModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      requestId,
    }: {
      orderId: number;
      requestId: number;
    }) => api.delete(`/orders/${orderId}/modifications/${requestId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-modifications'] }),
  });
}

// ---------------------------------------------------------------------------
// Quick Reorder -- Recent Order Items
// ---------------------------------------------------------------------------

export interface RecentOrderItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  last_ordered_date: string;
  last_quantity: number;
  unit_price: string;
  order_count: number;
}

export function useRecentOrderItems(limit = 50) {
  return useQuery({
    queryKey: ['recent-order-items', limit],
    queryFn: () =>
      fetchOne<{ items: RecentOrderItem[] }>('/orders/recent-items', { limit }),
  });
}

// ---------------------------------------------------------------------------
// Recurring Orders (typed)
// ---------------------------------------------------------------------------

export function useMyRecurringOrders(params?: {
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['my-recurring-orders', params],
    queryFn: () =>
      fetchOne<{ items: RecurringOrder[]; total: number }>(
        '/recurring-orders/mine',
        params,
      ),
  });
}

export function useRecurringOrder(id: number) {
  return useQuery({
    queryKey: ['recurring-order', id],
    queryFn: () => fetchOne<RecurringOrder>(`/recurring-orders/${id}`),
    enabled: id > 0,
  });
}

export function useCreateRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      frequency: string;
      day_of_week?: number;
      day_of_month?: number;
      auto_confirm?: boolean;
      items: { product_id: number; quantity: number }[];
    }) => api.post('/recurring-orders', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useUpdateRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      name?: string;
      frequency?: string;
      day_of_week?: number;
      day_of_month?: number;
      auto_confirm?: boolean;
    }) => api.patch(`/recurring-orders/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-recurring-orders'] });
      qc.invalidateQueries({ queryKey: ['recurring-order'] });
    },
  });
}

export function usePauseRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/recurring-orders/${id}/pause`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useResumeRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/recurring-orders/${id}/resume`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useAddRecurringOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      product_id: number;
      quantity: number;
    }) => api.post(`/recurring-orders/${id}/items`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-order'] }),
  });
}

export function useRemoveRecurringOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId }: { id: number; itemId: number }) =>
      api.delete(`/recurring-orders/${id}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-order'] }),
  });
}

export function useRecurringOrderHistory(id: number) {
  return useQuery({
    queryKey: ['recurring-order-history', id],
    queryFn: () =>
      fetchOne<{
        items: {
          id: number;
          generated_at: string;
          order_id: number | null;
          status: string;
        }[];
      }>(`/recurring-orders/${id}/history`),
    enabled: id > 0,
  });
}

// Untyped recurring orders (legacy endpoint)
export function useRecurringOrders(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['recurring-orders', params],
    queryFn: () =>
      fetchOne<{ items: RecurringOrder[]; total: number }>(
        '/recurring-orders',
        params,
      ),
  });
}

// ---------------------------------------------------------------------------
// Order Templates
// ---------------------------------------------------------------------------

export function useOrderTemplates() {
  return useQuery({
    queryKey: ['order-templates'],
    queryFn: () => fetchOne<{ templates: OrderTemplate[] }>('/order-templates'),
  });
}

export function useOrderTemplate(name: string) {
  return useQuery({
    queryKey: ['order-templates', name],
    queryFn: () =>
      fetchOne<OrderTemplate>(`/order-templates/${encodeURIComponent(name)}`),
    enabled: !!name,
  });
}

export function useCreateOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      items: { product_id: number; quantity: number }[];
    }) => api.post('/order-templates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function useUpdateOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      ...data
    }: {
      name: string;
      items: { product_id: number; quantity: number }[];
    }) =>
      api.put(`/order-templates/${encodeURIComponent(name)}`, { name, ...data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function useDeleteOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.delete(`/order-templates/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function usePlaceOrderFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      delivery_date,
    }: {
      name: string;
      delivery_date: string;
    }) =>
      api.post(`/order-templates/${encodeURIComponent(name)}/place-order`, {
        delivery_date,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Par Levels
// ---------------------------------------------------------------------------

export function useParLevels() {
  return useQuery({
    queryKey: ['par-levels'],
    queryFn: () => fetchOne<{ par_levels: ParLevelConfig[] }>('/par-levels'),
  });
}

export function useSetParLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      product_id: number;
      par_level: number;
      reorder_qty?: number;
    }) => api.post('/par-levels', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['par-levels'] }),
  });
}

export function useRemoveParLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => api.delete(`/par-levels/${productId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['par-levels'] }),
  });
}

export function useCheckBelowPar() {
  return useQuery({
    queryKey: ['par-levels', 'check'],
    queryFn: () => fetchOne<{ below_par: ParCheckResult[] }>('/par-levels/check'),
    enabled: false,
  });
}

export function useCreateParOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      items: { product_id: number; quantity: number }[];
      delivery_date: string;
    }) => api.post('/par-levels/create-order', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Delivery Calendar
// ---------------------------------------------------------------------------

export function useDeliveryCalendar(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['delivery-calendar', startDate, endDate],
    queryFn: () =>
      fetchOne<{ events: DeliveryCalendarEvent[] }>('/delivery-calendar', {
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: !!startDate && !!endDate,
  });
}

export function useDeliveryWindows() {
  return useQuery({
    queryKey: ['delivery-calendar', 'windows'],
    queryFn: () => fetchOne<{ windows: unknown[] }>('/delivery-calendar/windows'),
  });
}

export function useRequestReschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      order_id: number;
      new_date: string;
      notes?: string;
    }) => api.post('/delivery-calendar/reschedule', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-calendar'] }),
  });
}

// ---------------------------------------------------------------------------
// Delivery Feedback
// ---------------------------------------------------------------------------

export function useSubmitDeliveryFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      order_id: number;
      overall_rating: number;
      product_ratings?: unknown[];
      photo_urls?: string[];
      comments?: string;
      issues?: Record<string, boolean>;
    }) => api.post('/delivery-feedback', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-feedback'] }),
  });
}

export function useDeliveryFeedbackHistory(limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['delivery-feedback', limit, offset],
    queryFn: () =>
      fetchOne<{ feedback: DeliveryFeedbackData[] }>('/delivery-feedback', {
        limit,
        offset,
      }),
  });
}

export function useQualityTrends(months = 6) {
  return useQuery({
    queryKey: ['delivery-feedback', 'trends', months],
    queryFn: () =>
      fetchOne<QualityTrend>('/delivery-feedback/trends', { months }),
  });
}

// ---------------------------------------------------------------------------
// Claims & Returns
// ---------------------------------------------------------------------------

export function useClaims(status?: string) {
  return useQuery({
    queryKey: ['claims', status],
    queryFn: () => fetchOne<{ claims: ClaimSummary[] }>('/claims', { status }),
  });
}

export function useFileQualityClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      invoice_id?: number;
      claim_type: string;
      items?: unknown[];
      photos?: string[];
      amount: number;
      description?: string;
    }) => api.post('/claims/quality', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims'] }),
  });
}

export function useFileReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      order_id: number;
      items: { product_id: number; quantity: number }[];
      reason_code?: string;
      photos?: string[];
      notes?: string;
    }) => api.post('/claims/return', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims'] }),
  });
}

export function useClaimDetail(type: string, id: number) {
  return useQuery({
    queryKey: ['claims', type, id],
    queryFn: () => fetchOne<ClaimDetail>(`/claims/${type}/${id}`),
    enabled: !!type && !!id,
  });
}

// ---------------------------------------------------------------------------
// Substitution Preferences
// ---------------------------------------------------------------------------

export function useSubstitutionPreferences() {
  return useQuery({
    queryKey: ['substitution-preferences'],
    queryFn: () =>
      fetchOne<{ preferences: SubstitutionPref[] }>(
        '/substitution-preferences',
      ),
  });
}

export function useSetSubstitutionPref() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      product_id: number;
      allowed: boolean;
      acceptable_ids?: number[];
      reject_ids?: number[];
      organic_only?: boolean;
      max_price_pct?: number;
      notes?: string;
    }) => api.post('/substitution-preferences', data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['substitution-preferences'] }),
  });
}

export function useRemoveSubstitutionPref() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) =>
      api.delete(`/substitution-preferences/${productId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['substitution-preferences'] }),
  });
}

export function useSubstitutionHistory(limit = 20) {
  return useQuery({
    queryKey: ['substitution-preferences', 'history', limit],
    queryFn: () =>
      fetchOne<{ history: SubstitutionHistoryItem[] }>(
        '/substitution-preferences/history',
        { limit },
      ),
  });
}

// Substitution Rules (untyped legacy — now typed)
export function useSubstitutionRules(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['substitution-rules', params],
    queryFn: () =>
      fetchOne<{ items: SubstitutionRule[]; total: number }>(
        '/substitutions',
        params,
      ),
  });
}

// ---------------------------------------------------------------------------
// Shortage Alerts
// ---------------------------------------------------------------------------

export function useShortageAlerts(status?: string) {
  return useQuery({
    queryKey: ['shortage-alerts', status],
    queryFn: () =>
      fetchOne<{ alerts: ShortageAlertData[] }>('/shortage-alerts', { status }),
  });
}

export function useAcknowledgeShortageAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      api.post(`/shortage-alerts/${alertId}/acknowledge`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

export function useAcceptShortageSubstitute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      alertId,
      substitute_product_id,
    }: {
      alertId: number;
      substitute_product_id: number;
    }) =>
      api.post(`/shortage-alerts/${alertId}/accept-substitute`, {
        substitute_product_id,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

export function useDismissShortageAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      api.post(`/shortage-alerts/${alertId}/dismiss`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

// ---------------------------------------------------------------------------
// Live GPS Tracking
// ---------------------------------------------------------------------------

export function useLiveTracking(orderId: number) {
  return useQuery({
    queryKey: ['tracking', orderId, 'live'],
    queryFn: () => fetchOne<LiveTrackingData>(`/tracking/${orderId}/live`),
    enabled: !!orderId,
    refetchInterval: 30000,
  });
}

// ---------------------------------------------------------------------------
// Order Guides (location-specific)
// ---------------------------------------------------------------------------

export function useOrderGuides(addressId?: number) {
  return useQuery({
    queryKey: ['order-guides', addressId],
    queryFn: () =>
      fetchOne<{ guides: OrderTemplate[] }>('/order-guides', {
        address_id: addressId,
      }),
  });
}

export function useCreateOrderGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      items: { product_id: number; quantity?: number }[];
      address_id?: number;
    }) => api.post('/order-guides', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-guides'] }),
  });
}

export function useDeleteOrderGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/order-guides/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-guides'] }),
  });
}

export function useGuideToCart() {
  return useMutation({
    mutationFn: (id: number) => api.post(`/order-guides/${id}/to-cart`),
  });
}
